/**
 * Утилита для парсинга ошибок таблиц и преобразования их в понятные сообщения
 */

// Интерфейс для axios error
interface AxiosLikeError extends Error {
  code?: string;
  response?: {
    status?: number;
    data?: {
      error?: string;
      userMessage?: string;
      correlationId?: string;
      operationId?: string;
    };
  };
}

export interface ParsedTableError {
  title: string;
  message: string;
  canRetry: boolean;
  severity: 'error' | 'warning';
  technicalDetails?: {
    originalMessage?: string;
    statusCode?: number;
    correlationId?: string;
    operationId?: string;
    stack?: string;
  };
}

/**
 * Парсит ошибку и возвращает понятное описание для пользователя
 */
export function parseTableError(error: unknown): ParsedTableError {
  // Значения по умолчанию
  let title = 'Ошибка загрузки данных';
  let message = 'Не удалось загрузить данные таблицы. Попробуйте обновить страницу.';
  let canRetry = true;
  let severity: 'error' | 'warning' = 'error';

  const err = error as AxiosLikeError;
  
  const technicalDetails: ParsedTableError['technicalDetails'] = {
    originalMessage: err?.message || String(error),
    stack: err?.stack,
  };

  // Извлекаем данные из Axios error
  const response = err?.response;
  const statusCode = response?.status;
  const responseData = response?.data;

  if (statusCode) {
    technicalDetails.statusCode = statusCode;
  }

  if (responseData?.correlationId) {
    technicalDetails.correlationId = responseData.correlationId;
  }

  if (responseData?.operationId) {
    technicalDetails.operationId = responseData.operationId;
  }

  // Network errors
  if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network Error')) {
    title = 'Нет связи с сервером';
    message = 'Проверьте подключение к интернету и попробуйте снова.';
    canRetry = true;
    severity = 'error';
    return { title, message, canRetry, severity, technicalDetails };
  }

  // Timeout errors
  if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
    title = 'Превышено время ожидания';
    message = 'Сервер не отвечает. Попробуйте повторить запрос позже.';
    canRetry = true;
    severity = 'warning';
    return { title, message, canRetry, severity, technicalDetails };
  }

  // HTTP status codes
  if (statusCode) {
    switch (statusCode) {
      case 400:
        title = 'Некорректный запрос';
        message = responseData?.error || responseData?.userMessage || 'Проверьте введенные данные и попробуйте снова.';
        canRetry = false;
        severity = 'warning';
        break;

      case 401:
        title = 'Требуется авторизация';
        message = 'Ваша сессия истекла. Обновите страницу для повторной авторизации.';
        canRetry = false;
        severity = 'error';
        break;

      case 403:
        title = 'Доступ запрещен';
        message = 'У вас нет прав для просмотра этих данных. Обратитесь к администратору.';
        canRetry = false;
        severity = 'error';
        break;

      case 404:
        title = 'Данные не найдены';
        message = 'Запрошенные данные не найдены на сервере.';
        canRetry = true;
        severity = 'warning';
        break;

      case 409:
        title = 'Конфликт данных';
        message = responseData?.userMessage || 'Данные были изменены. Обновите страницу.';
        canRetry = true;
        severity = 'warning';
        break;

      case 500:
      case 502:
      case 503:
      case 504: {
        title = 'Ошибка сервера';
        
        const errorMessage = responseData?.error || err?.message || '';
        
        // Специфичные ошибки PostgreSQL
        if (errorMessage.includes('does not exist')) {
          if (errorMessage.includes('relation')) {
            title = 'Таблица БД не найдена';
            message = 'Требуется обновление схемы базы данных. Обратитесь к администратору системы.';
            canRetry = false;
            severity = 'error';
          } else if (errorMessage.includes('column')) {
            title = 'Поле БД не найдено';
            message = 'Структура базы данных устарела. Обратитесь к администратору системы.';
            canRetry = false;
            severity = 'error';
          }
        } else if (errorMessage.includes('permission denied')) {
          title = 'Нет прав доступа к данным БД';
          message = 'Недостаточно прав для выполнения операции. Обратитесь к администратору.';
          canRetry = false;
          severity = 'error';
        } else if (errorMessage.includes('connection') || errorMessage.includes('connect')) {
          title = 'Нет соединения с БД';
          message = 'Сервер не может подключиться к базе данных. Попробуйте позже.';
          canRetry = true;
          severity = 'error';
        } else if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
          title = 'Превышено время ожидания БД';
          message = 'База данных не отвечает. Попробуйте повторить запрос позже.';
          canRetry = true;
          severity = 'warning';
        } else {
          // Generic 500 error
          message = responseData?.userMessage || 'Произошла ошибка на сервере. Попробуйте повторить запрос позже.';
          canRetry = true;
          severity = 'error';
        }
        break;
      }

      default:
        title = `Ошибка ${statusCode}`;
        message = responseData?.userMessage || responseData?.error || 'Произошла непредвиденная ошибка.';
        canRetry = true;
        severity = 'error';
    }

    return { title, message, canRetry, severity, technicalDetails };
  }

  // React Query errors или другие ошибки без HTTP статуса
  if (err?.message) {
    const errorMsg = err.message.toLowerCase();
    
    if (errorMsg.includes('failed to fetch') || errorMsg.includes('fetch')) {
      title = 'Ошибка загрузки';
      message = 'Не удалось получить данные. Проверьте соединение с сервером.';
      canRetry = true;
      severity = 'error';
    } else if (errorMsg.includes('render') || errorMsg.includes('component')) {
      title = 'Ошибка отображения';
      message = 'Не удалось отобразить данные. Обновите страницу.';
      canRetry = true;
      severity = 'error';
    }
  }

  return { title, message, canRetry, severity, technicalDetails };
}

/**
 * Форматирует технические детали для отображения в dev mode
 */
export function formatTechnicalDetails(details?: ParsedTableError['technicalDetails']): string {
  if (!details) return 'Детали недоступны';

  const parts: string[] = [];

  if (details.originalMessage) {
    parts.push(`Сообщение: ${details.originalMessage}`);
  }

  if (details.statusCode) {
    parts.push(`HTTP статус: ${details.statusCode}`);
  }

  if (details.correlationId) {
    parts.push(`Correlation ID: ${details.correlationId}`);
  }

  if (details.operationId) {
    parts.push(`Operation ID: ${details.operationId}`);
  }

  if (details.stack && import.meta.env.DEV) {
    parts.push(`\nStack trace:\n${details.stack}`);
  }

  return parts.join('\n');
}


